import { MoreOutlined, PushpinFilled } from "@ant-design/icons"
import { App, Avatar, Button, Dropdown, Flex, Table, type TableProps, Typography } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"

import { TABLE_SURFACES } from "../../../../components/EntityTable/surfaces.ts"
import { ResizableHeaderCell } from "../../../../components/Table/ResizableHeaderCell.tsx"
import { useColumnResize } from "../../../../hooks/use-column-resize.tsx"
import { useDateFormat } from "../../../../hooks/use-date-format.ts"
import { useTableColumnConfig } from "../../../../hooks/use-table-column-config.tsx"
import { type LeadNote } from "../../types/lead.types.ts"
import { parseActivityMoment } from "../activity/lead-activity-date.ts"
import { useLeadActivityFormat } from "../activity/use-lead-activity-format.ts"
import { OVERVIEW_ACCENTS } from "../overview/OverviewCardIcon.tsx"
import { initialsOf } from "../overview/overview-initials.ts"
import { accentForAuthor } from "./note-author-accent.ts"
import { noteCreatedInstant } from "./note-created-instant.ts"
import { NoteCategoryChip } from "./NoteCategoryChip.tsx"

const { Text, Paragraph } = Typography

/** Falls back to a human date ("Sep 15, 2026") rather than the app-wide
 *  `DATE_FORMAT` constant's raw ISO shape, which reads poorly stacked over
 *  a time on a narrow table cell. The account's own format, when set, still
 *  wins — this only covers the no-preference case. */
const HUMAN_DATE_FALLBACK = "MMM D, YYYY"

interface NotesListViewProps {
  notes: LeadNote[]
  canEdit?: boolean
  onEdit: (note: LeadNote) => void
  onDelete: (id: string) => void
  onTogglePin: (note: LeadNote) => void
}

/** Renders the exact same pinned-first/newest-first array `LeadNotesSection`
 *  computed — no independent re-sort here. */
function NotesListView({
  notes,
  canEdit,
  onEdit,
  onDelete,
  onTogglePin
}: NotesListViewProps) {
  const { t } = useTranslation()
  const { modal } = App.useApp()
  const { userFormat } = useDateFormat()
  const { timezone } = useLeadActivityFormat()

  const confirmDelete = (note: LeadNote) => {
    modal.confirm({
      title: t("leadWorkspace.cards.notes.deleteConfirmTitle"),
      content: t("leadWorkspace.cards.notes.deleteConfirmBody"),
      okText: t("leadWorkspace.cards.notes.deleteOk"),
      okButtonProps: { danger: true },
      cancelText: t("leadWorkspace.card.cancel"),
      onOk: () => onDelete(note.id)
    })
  }

  const fullColumns: TableProps<LeadNote>["columns"] = [
        {
          key: "author",
          title: t("leadWorkspace.cards.notes.list.author"),
          width: 170,
          render: (_: unknown, note: LeadNote) => {
            const authorName: string =
              note.author?.name || t("leadWorkspace.cards.notes.unknownAuthor")
            const authorSeed: string = note.author?.id || authorName
            const accent = OVERVIEW_ACCENTS[accentForAuthor(authorSeed)]

            return (
              <Flex gap={8} align={"center"} className={"min-w-0"}>
                <Avatar
                  size={24}
                  className={"shrink-0"}
                  style={{
                    backgroundColor: accent.background,
                    color: accent.color
                  }}
                >
                  {initialsOf(authorName)}
                </Avatar>
                <Text
                  ellipsis={{ tooltip: authorName }}
                  className={"min-w-0 whitespace-nowrap"}
                >
                  {authorName}
                </Text>
              </Flex>
            )
          }
        },
        {
          key: "category",
          title: t("leadWorkspace.cards.notes.list.category"),
          width: 150,
          render: (_: unknown, note: LeadNote) => (
            <NoteCategoryChip category={note.category} variant={"chip"} />
          )
        },
        {
          key: "content",
          title: t("leadWorkspace.cards.notes.list.content"),
          render: (_: unknown, note: LeadNote) => (
            <Paragraph
              ellipsis={{ rows: 2, tooltip: note.content }}
              className={"mb-0 whitespace-pre-wrap"}
            >
              {note.content}
            </Paragraph>
          )
        },
        {
          key: "date",
          title: t("leadWorkspace.cards.notes.list.date"),
          width: 130,
          render: (_: unknown, note: LeadNote) => {
            const created = parseActivityMoment(
              noteCreatedInstant(note.created),
              userFormat,
              timezone
            )
            if (!created) return null
            return (
              <Flex vertical gap={0} className={"whitespace-nowrap"}>
                <Text className={"text-sm"}>
                  {created.format(userFormat || HUMAN_DATE_FALLBACK)}
                </Text>
                <Text type={"secondary"} className={"text-xs"}>
                  {created.format("h:mm A")}
                </Text>
              </Flex>
            )
          }
        },
        {
          key: "pinned",
          title: t("leadWorkspace.cards.notes.list.pinned"),
          width: 85,
          align: "center",
          render: (_: unknown, note: LeadNote) =>
            note.is_pinned ? (
              <PushpinFilled
                className={"text-amber-600"}
                aria-label={t("leadWorkspace.cards.notes.pinned")}
              />
            ) : null
        },
        {
          key: "actions",
          title: t("leadWorkspace.cards.notes.list.actions"),
          width: 75,
          align: "center",
          render: (_: unknown, note: LeadNote) =>
            canEdit ? (
              <Dropdown
                trigger={["click"]}
                menu={{
                  items: [
                    {
                      key: "edit",
                      label: t("leadWorkspace.cards.notes.edit"),
                      onClick: () => onEdit(note)
                    },
                    {
                      key: "pin",
                      label: t(
                        note.is_pinned
                          ? "leadWorkspace.cards.notes.unpin"
                          : "leadWorkspace.cards.notes.pin"
                      ),
                      onClick: () => onTogglePin(note)
                    },
                    {
                      key: "delete",
                      danger: true,
                      label: t("leadWorkspace.cards.notes.delete"),
                      onClick: () => confirmDelete(note)
                    }
                  ]
                }}
              >
                <Button
                  size={"small"}
                  type={"text"}
                  icon={<MoreOutlined />}
                  aria-label={t("leadWorkspace.cards.notes.moreActions")}
                />
              </Dropdown>
            ) : null
        }
  ]

  const { columns, fullConfig, setColumns } = useTableColumnConfig(
    TABLE_SURFACES.leadNotes,
    "default",
    fullColumns
  )
  const { tableWrapRef, decorateColumns } = useColumnResize<LeadNote>({
    fullConfig,
    setColumns
  })

  return (
    <div ref={tableWrapRef}>
      <Table<LeadNote>
        data-testid={"notes-list-view"}
        size={"small"}
        rowKey={"id"}
        pagination={false}
        dataSource={notes}
        tableLayout={"fixed"}
        scroll={{ x: 860 }}
        onRow={(note) =>
          ({
            "data-testid": `notes-list-row-${note.id}`
          }) as React.HTMLAttributes<HTMLTableRowElement>
        }
        columns={decorateColumns(columns)}
        components={{ header: { cell: ResizableHeaderCell } }}
      />
    </div>
  )
}

export { NotesListView }
